import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

const BookingContext = createContext();

const INITIAL_STATE = {
    item: null,
    type: 'room',
    dates: {
        checkIn: '',
        checkOut: '',
    },
    guests: {
        adults: 1,
        children: 0,
        infants: 0,
    },
    rooms: 1,
    customer: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        specialRequests: ''
    },
    discount: {
        type: 'none',
        value: 0,
        code: '',
        promo_code_id: null
    },
    payment: null,
    confirmation: null
};

export const BookingProvider = ({ children }) => {
    const [bookingState, setBookingState] = useState(INITIAL_STATE);

    const setBookingItem = useCallback((item, type, dates, totalGuests, details = {}) => {
        setBookingState(prev => ({
            ...prev,
            item,
            type,
            dates: { ...prev.dates, ...dates },
            totalGuests: Number(totalGuests) || 1,
            guests: {
                adults: Number(details.adults) || 1,
                children: Number(details.children) || 0,
                infants: Number(details.infants) || 0,
            },
            rooms: Number(details.rooms) || 1,
            childAges: details.childAges || []
        }));
    }, []);

    const updateCustomerInfo = useCallback((info) => {
        setBookingState(prev => ({
            ...prev,
            customer: { ...prev.customer, ...info }
        }));
    }, []);

    const updatePaymentInfo = useCallback((payment) => {
        setBookingState(prev => ({ ...prev, payment }));
    }, []);

    const updateConfirmation = useCallback((confirmation) => {
        setBookingState(prev => ({ ...prev, confirmation }));
    }, []);

    const updateDiscount = useCallback((discount) => {
        setBookingState(prev => ({ ...prev, discount }));
    }, []);

    const clearBooking = useCallback(() => {
        setBookingState(INITIAL_STATE);
    }, []);

    const value = useMemo(() => ({
        bookingState,
        setBookingItem,
        updateCustomerInfo,
        updatePaymentInfo,
        updateConfirmation,
        updateDiscount,
        clearBooking
    }), [bookingState, setBookingItem, updateCustomerInfo, updatePaymentInfo, updateConfirmation, clearBooking]);

    return (
        <BookingContext.Provider value={value}>
            {children}
        </BookingContext.Provider>
    );
};

BookingProvider.propTypes = {
    children: PropTypes.node.isRequired
};

export const  useBooking = () => {
    const context = useContext(BookingContext);
    if (!context) {
        throw new Error('useBooking must be used within a BookingProvider');
    }
    return context;
};
